/* eslint-disable jsx-a11y/control-has-associated-label */

/* eslint-disable jsx-a11y/media-has-caption */

/* eslint-disable import/no-extraneous-dependencies */
import {
  allVideoElementWithoutYoutube,
  getVideoSrc,
  getVideoThumb,
  sortVideosWithLiveTourFirst,
} from "../SectionFeatured.tsx/utils";
import classes from "./VideoSection.module.scss";
import VideoPlayer from "@Components/VideoPlayer";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useRef, useState } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { AppState } from "@Store/index";
import useTranslationHook from "hooks/useTranslationHook";
import config from "@Config/index";
import wrapperHOC from "@Utils/wrapperHOC";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import extractVideoId from "@Pages/VideoPage/utils/extractVideoId";
import { addLanguageToPath } from "@Utils/languageCore";
import { getVideoPageSuccess } from "@Pages/VideoPage/data/videoPageReducer";

const VideoContainer = () => {
  const { data, reviewData, userLanguage } = useSelector(
    ({ propertyPageData, appData }: AppState) => ({
      data: propertyPageData.data,
      reviewData: propertyPageData.reviewData,
      userLanguage: appData.userLanguage || "en",
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);

  const playerRefs = useRef<any>({});
  const [playingVideos, setPlayingVideos] = useState<{ [key: number]: boolean }>({});
  const canonicalName = data?.canonical_name || "";

  const showVideoTour: any = allVideoElementWithoutYoutube(data);
  const items = showVideoTour;
  const carouselRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslationHook();

  const handleVideoClick = (index: number) => {
    gtagEventEmitter({ action: "VO_Expanded" });
    const video = items[index];
    const videoId = extractVideoId(video?.path);
    if (canonicalName && videoId) {
      // Build filtered videos array (exclude YouTube and vo_tour — same filter as video tour section)
      const allVideos: any[] = [];
      if (data?.videos?.length > 0) {
        allVideos.push(
          ...data.videos
            .filter((v: any) => !v?.path?.includes("youtube") && v?.type !== "vo_tour" && v?.type)
            .map((v: any) => ({ ...v, associatedRoomType: null })),
        );
      }
      data?.children_short?.forEach((child: any) => {
        if (child.videos?.length > 0) {
          allVideos.push(
            ...child.videos
              .filter((v: any) => !v?.path?.includes("youtube") && v?.type !== "vo_tour" && v?.type)
              .map((v: any) => ({
                ...v,
                associatedRoomType: {
                  name: child.name,
                  id: child.id,
                  meta: child.meta,
                  images: child.images,
                  pricing: child.pricing,
                  features: child.features,
                },
              })),
          );
        }
      });
      // Build review data from propertyPageData.reviewData
      const reviews = [...(reviewData?.result || []), ...(reviewData?.resultWithoutMedia || [])];
      // Populate videoPageData in Redux (same as gallery does)
      dispatch(
        getVideoPageSuccess({
          data: {
            id: data?.id,
            canonical_name: data?.canonical_name,
            name: data?.name,
            reviews_rating: data?.reviews_rating,
            reviews_count: data?.reviews_count,
            images: data?.images?.slice(0, 1) || [],
          },
          reviewData: reviews,
          videos: sortVideosWithLiveTourFirst(allVideos),
          selectedVideoId: videoId,
          dataSource: "gallery",
        }),
      );
      propertyPageDispatcher.showVideoModal({ show: true, videoId });
      if (__CLIENT__) {
        window.history.pushState(
          { videoModal: true },
          "",
          addLanguageToPath(`/places/${canonicalName}/video/${videoId}`, userLanguage),
        );
      }
    }
  };

  return (
    <>
      <div className={classes.titleContainer}>
        {config.IS_CHINA_APP ? (
          <p data-testid="property-virtual-walkthrough" className={classes.title}>
            {t("propertypage.components.videosection.stepinsideproperty")}
          </p>
        ) : (
          <p
            data-testid="property-virtual-walkthrough"
            className={classes.title}
            dangerouslySetInnerHTML={{
              __html: `${t(
                "propertypage.components.videosection.stepinsideproperty_prefix",
              )} <span>${t(
                "propertypage.components.videosection.stepinsideproperty_highlight",
              )}</span>`,
            }}
          />
        )}
        <div className={classes.shortDescription}>
          {t("propertypage.components.videosection.virtualwalkthroughdescription")}
        </div>
      </div>
      <div className={classes.carousel}>
        <div className={classes.carouselInner} ref={carouselRef}>
          {items.map((item: any, index: any) => {
            // Create a ref object for this video
            if (!playerRefs.current[index]) {
              playerRefs.current[index] = { current: null };
            }

            return (
              <div
                key={`video-wrapper-${index + 1}`}
                className={classes.videoContainerWrapper}
                onClick={(e) => {
                  if (!playingVideos[index]) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleVideoClick(index);
                  }
                }}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !playingVideos[index]) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleVideoClick(index);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Play video"
              >
                <VideoPlayer
                  key={`video-player-${index}`}
                  id={`video-${index + 1}`}
                  name="virtual_video"
                  customRef={playerRefs.current[index]}
                  videoUrl={getVideoSrc(item, 500)}
                  inLoop
                  height={154}
                  playsInline
                  muted
                  isLazyLoaded
                  hasClickOverlay={false}
                  noControls={!playingVideos[index]}
                  onPlaying={(playing) => {
                    setPlayingVideos((prev) => ({ ...prev, [index]: playing }));
                  }}
                  posterUrl={getVideoThumb(item)}
                  containerClassName={formatClassNames(
                    classes.container,
                    playingVideos[index] && classes.containerPlaying,
                  )}
                  videoPlayerClassName={classes.video}
                />
                {!playingVideos[index] && (
                  <>
                    <div className={classes.playButton}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="26"
                        height="26"
                        viewBox="0 0 26 26"
                        fill="none"
                      >
                        <path
                          d="M12.8 2.4C18.545 2.4 23.2 7.055 23.2 12.8C23.2 18.545 18.545 23.2 12.8 23.2C7.055 23.2 2.4 18.545 2.4 12.8C2.4 7.055 7.055 2.4 12.8 2.4ZM12.8 25.6C19.87 25.6 25.6 19.87 25.6 12.8C25.6 5.73 19.87 0 12.8 0C5.73 0 0 5.73 0 12.8C0 19.87 5.73 25.6 12.8 25.6ZM10.625 7.375C10.255 7.15 9.79 7.14 9.41 7.35C9.03 7.56 8.8 7.965 8.8 8.4V17.2C8.8 17.635 9.035 18.035 9.415 18.245C9.795 18.455 10.255 18.45 10.63 18.22L17.83 13.82C18.185 13.6 18.405 13.215 18.405 12.795C18.405 12.375 18.185 11.99 17.83 11.77L10.63 7.37L10.625 7.375ZM14.9 12.8L11.2 15.06V10.54L14.9 12.8Z"
                          fill="var(--Secondary-neutral-00, #FFF)"
                        />
                      </svg>
                    </div>
                    {/* <div className={classes.videoOverlayInfo}>
                      <div className={classes.videoTitle}>Property Tour</div>
                      <div className={classes.videoDuration}>
                        {item?.duration
                          ? `${Math.floor(parseFloat(item.duration) / 60)}:${String(
                              Math.floor(parseFloat(item.duration) % 60),
                            ).padStart(2, "0")}`
                          : "0:00"}
                      </div>
                    </div> */}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default wrapperHOC(VideoContainer, {
  componentName: "VideoContainer-PropertyPage-VideoSection",
  showForChina: true,
});
